import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { trackCtaClick } from '../utils/analytics';

const painPoints = [
  'Managing multiple vendors',
  'Coordinating across time zones',
  'Making repeated trips to India',
  'Lack of clarity during execution',
  'Handling last-minute issues',
];

const solutions = [
  'Concept & design direction',
  'Venue & vendor curation',
  'Budget planning & allocation',
  'End-to-end coordination',
  'On-ground execution & oversight',
];

const steps = [
  {
    num: '01',
    title: 'Structured Planning',
    desc: 'Clear timelines, curated options, and guided decisions.',
  },
  {
    num: '02',
    title: 'Seamless Communication',
    desc: 'Consistent updates, aligned without overwhelm.',
  },
  {
    num: '03',
    title: 'Complete Execution',
    desc: 'Every detail handled, from first conversation to final moment.',
  },
];

export default function NriSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const scrollToContact = () => {
    trackCtaClick('nri_section');
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="nri-section"
      ref={ref}
      className="bg-[#F5F0EC] py-20 md:py-36 px-6 md:px-16 lg:px-24"
    >
      <div className="max-w-[900px] mx-auto">

        {/* BLOCK 1 — Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center"
        >
          <span className="label-text" data-testid="nri-label">For Clients Abroad</span>
          <div className="editorial-line mx-auto mt-4 mb-10 md:mb-14" />
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#0A0A0A] leading-[1.3] max-w-[600px] mx-auto">
            Planning your celebration in India, from anywhere in the world.
          </h2>
        </motion.div>

        {/* BLOCK 2 — Subtext */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-8 md:mt-10 text-center max-w-[520px] mx-auto"
        >
          <p className="font-light text-[13px] sm:text-sm leading-[1.9] text-[#0A0A0A]/55" style={{ letterSpacing: '0.3px' }}>
            You don't need to manage vendors, coordinate timelines, or rely on family on the ground.
          </p>
          <p className="mt-2 font-light text-[13px] sm:text-sm leading-[1.9] text-[#0A0A0A]/55" style={{ letterSpacing: '0.3px' }}>
            We handle everything, end to end.
          </p>
        </motion.div>

        {/* Refined divider */}
        <div className="max-w-[120px] mx-auto my-12 md:my-16 border-t border-[#0A0A0A]/6" />

        {/* BLOCK 3 + 4 — Editorial Panels (Pain / Solution) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-[800px] mx-auto"
        >
          {/* LEFT PANEL — Problems (warmer tone) */}
          <div
            className="relative rounded-2xl px-7 py-8 md:px-8 md:py-9"
            style={{
              background: 'linear-gradient(135deg, rgba(216,180,160,0.08) 0%, rgba(239,234,230,0.5) 100%)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)',
            }}
          >
            <div className="absolute left-0 top-6 bottom-6 w-[2px] rounded-full bg-[#D8B4A0]/25" />
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#8C8C8C]/50 font-light mb-6 pl-2">
              You Don't Have to Worry About
            </p>
            <ul className="space-y-4 pl-2">
              {painPoints.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-[7px] w-[4px] h-[4px] rounded-full bg-[#D8B4A0]/40 shrink-0" />
                  <span className="font-light text-[13px] leading-[1.8] text-[#0A0A0A]/40" style={{ letterSpacing: '0.2px' }}>
                    {p}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT PANEL — Solutions (brighter, stronger contrast) */}
          <div
            className="relative rounded-2xl px-7 py-8 md:px-8 md:py-9"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(239,234,230,0.4) 100%)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 3px rgba(10,10,10,0.02)',
            }}
          >
            <div className="absolute right-0 top-6 bottom-6 w-[2px] rounded-full bg-[#D8B4A0]/15" />
            <p className="text-[9px] tracking-[0.3em] uppercase text-[#8C8C8C]/50 font-light mb-6">
              We Take Care of Everything
            </p>
            <ul className="space-y-4">
              {solutions.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <span className="mt-[7px] w-[4px] h-[4px] rounded-full bg-[#0A0A0A]/15 shrink-0" />
                  <span className="font-light text-[13px] leading-[1.8] text-[#0A0A0A]/60" style={{ letterSpacing: '0.2px' }}>
                    {s}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Refined divider */}
        <div className="max-w-[120px] mx-auto my-12 md:my-16 border-t border-[#0A0A0A]/6" />

        {/* BLOCK 5 — How We Work (cards with depth) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.25 }}
        >
          <p className="text-[9px] tracking-[0.3em] uppercase text-[#8C8C8C]/50 font-light mb-8 text-center">
            How We Work with You
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {steps.map((step) => (
              <div
                key={step.num}
                className="rounded-2xl px-7 py-7 transition-all duration-300 md:hover:-translate-y-1"
                style={{
                  background: 'linear-gradient(160deg, rgba(255,255,255,0.7) 0%, rgba(239,234,230,0.5) 100%)',
                  boxShadow: '0 4px 16px rgba(10,10,10,0.03), 0 1px 3px rgba(10,10,10,0.02), inset 0 1px 0 rgba(255,255,255,0.6)',
                }}
              >
                <span className="font-serif text-lg text-[#D8B4A0]/70">{step.num}</span>
                <h4 className="mt-3 text-[13px] tracking-[0.03em] text-[#0A0A0A]/70 font-normal">
                  {step.title}
                </h4>
                <p className="mt-2 font-light text-[12px] leading-[1.9] text-[#0A0A0A]/40" style={{ letterSpacing: '0.2px' }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* BLOCK 6 — Emotional close */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-14 md:mt-20 text-center"
        >
          <p className="font-serif text-base sm:text-lg italic leading-[2] text-[#0A0A0A]/60">
            You arrive.<br />
            Everything is ready.
          </p>
          <p className="mt-3 font-light text-[12px] leading-[1.9] text-[#0A0A0A]/35" style={{ letterSpacing: '0.3px' }}>
            Exactly as you imagined, or better.
          </p>
        </motion.div>

        {/* BLOCK 7 — Power line */}
        <div className="mt-10 md:mt-14 text-center">
          <p className="font-serif text-sm sm:text-base italic leading-[1.8] text-[#0A0A0A]/50">
            We are your team in India, so you don't have to be.
          </p>
        </div>

        {/* BLOCK 8 — CTA */}
        <div className="mt-10 md:mt-14 text-center">
          <button
            onClick={scrollToContact}
            data-testid="nri-cta-button"
            className="px-10 py-3.5 rounded-full border border-[#0A0A0A]/20 text-[#0A0A0A] text-[11px] tracking-[0.2em] uppercase font-light hover:bg-[#0A0A0A] hover:text-[#EFEAE6] transition-all duration-500"
          >
            Plan Your Event from Anywhere
          </button>
        </div>
      </div>
    </section>
  );
}
